import { yupResolver } from '@hookform/resolvers/yup';
import { Stack } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import {
  BenefitDocumentDto,
  BenefitDocumentType,
  EmployeeDto,
  OnboardingPeriodDto,
} from '@zorro/clients';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { formatDateISO, parseDateISO } from '@zorro/shared/formatters';
import { FormErrorMessage, Group, Icon, Text } from '@zorro/web/design-system';
import {
  callEndpoint,
  createBenefitDocument,
  downloadBenefitDocument,
  useForm,
  useIdempotencyKey,
  useLoadingOverlay,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { Control, Controller } from 'react-hook-form';
import * as yup from 'yup';

import { CoveragePeriodInput } from '../../CoveragePeriodInput';
import { FileUploader } from '../../FileUploader';
import { FormFooter } from '../../FormFooter';
import { UploadedFileBox } from '../../UploadedFileBox';

const waiveCoverageSchema = yup.object({
  effectiveDates: yup
    .tuple([yup.date().required().nullable(), yup.date().required().nullable()])
    .required(),
  waiveEvidence: yup.mixed<File | BenefitDocumentDto>().nullable(),
});

type WaiveCoverageFormFields = yup.InferType<typeof waiveCoverageSchema>;

type WaiveCoverageFormSingleProps = {
  employeeId: EmployeeDto['id'];
  employerId: string;
  onboardingPeriodId: OnboardingPeriodDto['id'];
  onSuccess: () => void;
  onClose: () => void;
};

export const WaiveCoverageFormSingle = ({
  employeeId,
  employerId,
  onboardingPeriodId,
  onSuccess,
  onClose,
}: WaiveCoverageFormSingleProps) => {
  'use no memo';
  const { startLoading, stopLoading } = useLoadingOverlay();

  const idempotencyKey = useIdempotencyKey();
  const { mutate: mutateWaiveCoverage } = useMonolithMutation({
    method: 'onboardingPeriodsControllerWaiveCoverage',
    successMessage: 'Waiver successfully submitted',
    shouldShowLoadingOverlay: false,
  });

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });

  const allBenefitDocuments = onboardingPeriod?.benefitDocuments ?? [];
  const previousWaiveEvidence = allBenefitDocuments.find(
    ({ type }) => type === BenefitDocumentType.WAIVE
  );

  const { control, getValues, setValue, watch, formState } =
    useForm<WaiveCoverageFormFields>({
      mode: 'all',
      defaultValues: {
        effectiveDates: onboardingPeriod
          ? [
              parseDateISO(onboardingPeriod.effectiveFrom).toDate(),
              parseDateISO(onboardingPeriod.effectiveUntil).toDate(),
            ]
          : [null, null],
        waiveEvidence: previousWaiveEvidence || null,
      },
      resolver: yupResolver(waiveCoverageSchema),
    });

  const { isValid, errors } = formState;

  const handleSubmit = async () => {
    try {
      const { waiveEvidence, effectiveDates } = getValues();
      const [effectiveFrom, effectiveUntil] = effectiveDates || [];

      if (!effectiveFrom || !effectiveUntil) {
        return;
      }

      startLoading();

      const newWaiveEvidence =
        waiveEvidence && waiveEvidence instanceof File
          ? await createBenefitDocument(
              employeeId,
              employerId,
              waiveEvidence,
              onboardingPeriodId,
              true,
              BenefitDocumentType.WAIVE
            )
          : undefined;

      await Promise.all(
        allBenefitDocuments
          .filter(({ type }) => type === BenefitDocumentType.WAIVE)
          .map((document) =>
            callEndpoint({
              method: 'benefitDocumentsControllerRemoveBenefitDocument',
              params: [document.id],
            })
          )
      );

      if (newWaiveEvidence) {
        await callEndpoint({
          method: 'benefitDocumentsControllerCreateBenefitDocument',
          params: [newWaiveEvidence],
        });
      }

      await mutateWaiveCoverage([
        onboardingPeriodId,
        idempotencyKey.get(),
        {
          employeeId,
          effectiveDates: {
            from: formatDateISO(effectiveFrom),
            until: formatDateISO(effectiveUntil),
          },
        },
      ]);

      onSuccess();
    } catch {
      /* empty */
    } finally {
      stopLoading();
    }
  };

  const waiveEvidenceField = watch('waiveEvidence');

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Submit waiver"
        primaryButtonProps={{
          onClick: handleSubmit,
          disabled: !isValid,
        }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{ onClick: onClose }}
      />
    ),
    [handleSubmit, isValid, onClose]
  );
  useDrawerFooter(drawerFooter);

  return (
    <Stack gap="xl">
      <Text c="zorroGray.4">Confirm coverage waiver details</Text>

      <Controller
        control={control}
        name="effectiveDates"
        render={({ field: { ...rest } }) => (
          <CoveragePeriodInput
            {...rest}
            label="Confirm waiver effective dates"
            onboardingPeriodId={onboardingPeriodId}
            employeeId={employeeId}
            required
          />
        )}
      />
      {!waiveEvidenceField && (
        <FileUploader
          control={control as unknown as Control}
          name="waiveEvidence"
          label="Waiver document"
          isLoading={false}
          onDrop={(files: File[], onBlur, onChange) => {
            onChange(files[0]);
            onBlur();
          }}
          buttonText="Upload document"
          zoneLabelText="Upload a document or screenshot as evidence of the waiver"
          isDisabled={Boolean(waiveEvidenceField)}
        />
      )}
      {waiveEvidenceField && (
        <UploadedFileBox
          label="Waiver document"
          onClickDelete={() =>
            setValue('waiveEvidence', null, { shouldValidate: true })
          }
          name={
            waiveEvidenceField instanceof File
              ? `Confirmation file: ${waiveEvidenceField.name}`
              : `Existing evidence: ${waiveEvidenceField.fileName}`
          }
          onClickDownload={
            waiveEvidenceField instanceof File
              ? undefined
              : () =>
                  downloadBenefitDocument(
                    waiveEvidenceField.documentUrl,
                    waiveEvidenceField.fileName
                  )
          }
        />
      )}

      <FormErrorMessage fieldName="waiveEvidence" errors={errors} />

      <Group wrap="nowrap" gap="xs">
        <Icon icon={IconInfoCircle} />
        <Text>
          Please make sure to collect evidence of the employee's waive election
          confirmation
        </Text>
      </Group>
    </Stack>
  );
};
